import { Link } from "react-router-dom";
import {
  Archive, ArchiveRestore, BookmarkPlus, CheckCheck, ExternalLink, Loader2, Star,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { FeedItem, FeedSubscription } from "@/lib/feedReader";
import { formatFeedDate } from "./feedItemReaderHelpers";
import { safeHref } from "@/lib/safeHref";

type Props = {
  item: FeedItem;
  subscription: FeedSubscription | null;
  workspaceLabel?: string | null;
  showWorkspaceChip?: boolean;
  isStarred: boolean;
  isArchived: boolean;
  isImported: boolean;
  isPromoting?: boolean;
  toneTitle: string;
  onStar: (item: FeedItem) => void;
  onArchive: (item: FeedItem) => void;
  onToggleRead: (item: FeedItem) => void;
  onPromote: (item: FeedItem) => void;
};

/** Header strip (meta line + title + actions toolbar) for FeedItemReader. */
export const FeedItemReaderHeader = ({
  item, subscription, workspaceLabel, showWorkspaceChip,
  isStarred, isArchived, isImported, isPromoting, toneTitle,
  onStar, onArchive, onToggleRead, onPromote,
}: Props) => (
  <div className="border-b border-border/60 px-5 py-4 space-y-3">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="truncate">{subscription?.title ?? "Feed"}</span>
      {item.author && (<><span>·</span><span className="truncate">{item.author}</span></>)}
      {item.published_at && (<><span>·</span><span>{formatFeedDate(item.published_at)}</span></>)}
      {showWorkspaceChip && workspaceLabel && (
        <Badge variant="outline" className="ml-auto text-xs">{workspaceLabel}</Badge>
      )}
    </div>
    <h2 className="text-lg font-semibold leading-snug" style={{ color: toneTitle }}>
      {(() => { const safeLink = safeHref(item.link); return safeLink ? (
        <a href={safeLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-baseline gap-1">
          {item.title || "(Untitled)"}
          <ExternalLink className="inline-block h-3.5 w-3.5 opacity-60 translate-y-0.5" />
        </a>
      ) : (
        item.title || "(Untitled)"
      ); })()}
    </h2>
    <div className="flex flex-wrap items-center gap-1">
      <Button size="sm" variant="ghost" onClick={() => onStar(item)} title={isStarred ? "Unstar (s)" : "Star (s)"}>
        <Star className="h-4 w-4" style={isStarred ? { fill: toneTitle, color: toneTitle } : undefined} />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onToggleRead(item)} title={item.read_at ? "Mark unread (r)" : "Mark read (r)"}>
        <CheckCheck className="h-4 w-4" style={item.read_at ? { color: toneTitle } : undefined} />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onArchive(item)} title={isArchived ? "Move to inbox (e)" : "Archive (e)"}>
        {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
      </Button>
      {isImported ? (
        <Button asChild size="sm" variant="ghost">
          <Link to={`/my-library?focus=${item.imported_to_library_item_id}`}>
            <BookmarkPlus className="h-4 w-4 mr-1" style={{ color: toneTitle }} /> View in library
          </Link>
        </Button>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => onPromote(item)} disabled={isPromoting}>
          {isPromoting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BookmarkPlus className="h-4 w-4 mr-1" />}
          Save to library
        </Button>
      )}
    </div>
  </div>
);
